import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { eyebrowClass, cardClass } from '@/lib/sectionStyles';

interface CoverageCard {
  title: string;
  description: string;
}

interface CoverageSection {
  eyebrow: string;
  title: string;
  cards: CoverageCard[];
}

interface GovernanceSection {
  eyebrow: string;
  title: string;
  body: string;
  linkTo: string;
  linkLabel: string;
}

export interface SolutionPageLayoutProps {
  title: string;
  canonicalPath: string;
  crumbLabel: string;
  heroTitle: string;
  /** Used as both the visible hero paragraph and the SEO meta description — the two have never needed to differ for a solution page. */
  heroSub: string;
  coverage: CoverageSection;
  governance: GovernanceSection;
}

/**
 * Shared layout for the Claims and Underwriting solution pages — their
 * mockup markup (pagehero + 6-card coverage grid + dark governance band)
 * is near-identical; no other page shares enough structure to justify this.
 */
export function SolutionPageLayout({
  title,
  canonicalPath,
  crumbLabel,
  heroTitle,
  heroSub,
  coverage,
  governance,
}: SolutionPageLayoutProps) {
  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={heroSub} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={heroSub} />
        <link rel="canonical" href={`https://www.criticalbridge.ai${canonicalPath}`} />
      </Helmet>

      {/* Hero */}
      <section className="bg-secondary">
        <div className="container mx-auto px-4 py-16 sm:py-20">
          <div className="font-mono text-xs text-secondary-foreground/60">
            <Link to="/" className="hover:text-secondary-foreground">
              Home
            </Link>{' '}
            / Solutions / {crumbLabel}
          </div>
          <h1 className="mt-4 max-w-[24ch] text-4xl font-extrabold leading-tight text-secondary-foreground sm:text-5xl">
            {heroTitle}
          </h1>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-secondary-foreground/70">
            {heroSub}
          </p>
          <Button size="lg" className="mt-8" asChild>
            <Link to="/contact">Book a working session</Link>
          </Button>
        </div>
      </section>

      {/* Coverage */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <span className={eyebrowClass}>{coverage.eyebrow}</span>
          <h2 className="mt-2 text-3xl font-bold">{coverage.title}</h2>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {coverage.cards.map((card) => (
              <div key={card.title} className={cardClass}>
                <h3 className="text-lg font-bold">{card.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">{card.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Governance */}
      <section className="bg-secondary py-16">
        <div className="container mx-auto px-4">
          <span className={eyebrowClass}>{governance.eyebrow}</span>
          <h2 className="mt-2 text-3xl font-bold text-secondary-foreground">{governance.title}</h2>
          <p className="mt-4 max-w-[68ch] text-secondary-foreground/70">{governance.body}</p>
          <Link
            to={governance.linkTo}
            className="mt-6 inline-block font-bold text-primary hover:underline"
          >
            {governance.linkLabel}
          </Link>
        </div>
      </section>
    </>
  );
}
